import { useState, useCallback, useEffect } from 'react';

interface PinPadProps {
	length?: number;
	onComplete: (pin: string) => void;
	error?: string;
	label?: string;
	testIdPrefix?: string;
}

export function PinPad({
	length = 4,
	onComplete,
	error,
	label = 'Enter PIN',
	testIdPrefix = 'pin-pad',
}: PinPadProps) {
	const [pin, setPin] = useState('');
	const [shaking, setShaking] = useState(false);

	const handleDigit = useCallback(
		(digit: string) => {
			if (pin.length >= length) return;
			const newPin = pin + digit;
			setPin(newPin);
			if (newPin.length === length) {
				onComplete(newPin);
			}
		},
		[pin, length, onComplete],
	);

	const handleDelete = useCallback(() => {
		setPin((prev) => prev.slice(0, -1));
	}, []);

	const handleClear = useCallback(() => {
		setPin('');
	}, []);

	// Trigger shake on error change
	useEffect(() => {
		if (error) {
			setShaking(true);
			setPin('');
			setTimeout(() => setShaking(false), 400);
		}
	}, [error]);

	return (
		<div
			className='flex flex-col items-center gap-6'
			data-testid={testIdPrefix}>
			<p className='text-body-lg text-text-secondary font-medium'>{label}</p>

			{/* Dot indicators */}
			<div
				className={`flex gap-3 ${shaking ? 'animate-shake' : ''}`}
				data-testid={`${testIdPrefix}-dots`}>
				{Array.from({ length }).map((_, i) => (
					<div
						key={i}
						className={`w-4 h-4 rounded-full transition-all duration-200 ${
							i < pin.length ? 'bg-brand-orange scale-110' : 'bg-surface-border'
						}`}
					/>
				))}
			</div>

			{error && (
				<p
					className='text-status-error text-body-sm'
					data-testid={`${testIdPrefix}-error`}>
					{error}
				</p>
			)}

			{/* Number pad */}
			<div className='grid grid-cols-3 gap-3 mt-2'>
				{[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
					<button
						key={digit}
						type='button'
						onClick={() => handleDigit(String(digit))}
						data-testid={`${testIdPrefix}-digit-${digit}`}
						className='w-[72px] h-[72px] rounded-full bg-surface-card border border-surface-border
              text-heading-2 text-text-primary font-semibold
              hover:bg-brand-orange-light hover:border-brand-orange
              active:scale-95 transition-all duration-150
              flex items-center justify-center shadow-sm'>
						{digit}
					</button>
				))}
				<button
					type='button'
					onClick={handleClear}
					data-testid={`${testIdPrefix}-clear`}
					className='w-[72px] h-[72px] rounded-full bg-surface text-body-sm text-text-muted
            hover:bg-surface-border transition-all flex items-center justify-center'>
					Clear
				</button>
				<button
					type='button'
					onClick={() => handleDigit('0')}
					data-testid={`${testIdPrefix}-digit-0`}
					className='w-[72px] h-[72px] rounded-full bg-surface-card border border-surface-border
            text-heading-2 text-text-primary font-semibold
            hover:bg-brand-orange-light hover:border-brand-orange
            active:scale-95 transition-all duration-150
            flex items-center justify-center shadow-sm'>
					0
				</button>
				<button
					type='button'
					onClick={handleDelete}
					data-testid={`${testIdPrefix}-delete`}
					className='w-[72px] h-[72px] rounded-full bg-surface text-body-sm text-text-muted
            hover:bg-surface-border transition-all flex items-center justify-center'>
					<svg
						width='24'
						height='24'
						viewBox='0 0 24 24'
						fill='none'
						stroke='currentColor'
						strokeWidth='2'
						strokeLinecap='round'
						strokeLinejoin='round'>
						<path d='M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z' />
						<line x1='18' y1='9' x2='12' y2='15' />
						<line x1='12' y1='9' x2='18' y2='15' />
					</svg>
				</button>
			</div>
		</div>
	);
}
